import { useRef, useState } from "react";
import { AlertCircle, FileText, Maximize2, Upload } from "lucide-react";
import { EVTX_MAX_BYTES, MAX_EVENT_LOGS_PER_SESSION, isEvtxFileName } from "@spectree/shared";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { EvtxSummaryViewDialog, type EvtxSummaryView } from "./evtx-summary-dialog";
import { useSummarizeInterviewEventLog, useUploadEventLog } from "@/hooks/queries/use-fogbugz";
import { cn } from "@/lib/utils";

/** Shown when a file is over EVTX_MAX_BYTES; the tester can still attach it in FogBugz. */
export const EVENT_LOG_TOO_LARGE_MESSAGE =
  "This log is larger than 10 MB. File the case first, then attach the log in FogBugz directly.";

/** Shown when a file does not end in .evtx. */
export const EVENT_LOG_WRONG_TYPE_MESSAGE =
  "Only Windows Event Log (.evtx) files can be attached here.";

/** Shown when the session already holds MAX_EVENT_LOGS_PER_SESSION logs. */
export const EVENT_LOG_LIMIT_MESSAGE = `You can attach up to ${String(MAX_EVENT_LOGS_PER_SESSION)} event logs to one report.`;

/** Shown when a new file is offered while an upload or summary is still in flight. */
export const EVENT_LOG_BUSY_MESSAGE = "Wait for the current event log to finish.";

const SUMMARIZE_CHECKBOX_ID = "event-log-summarize";

/**
 * Why `file` cannot be added as the next event log of a report holding
 * `alreadyAttached` logs, or null when it can: not `.evtx`, over
 * EVTX_MAX_BYTES, empty, or the per-report cap reached. Shared with the
 * Toolbox FogBugz Formulator (epic PI-SPEC-SW-E309), whose caps are the same
 * numbers. Pure; exported for tests.
 */
export function eventLogValidationError(file: { name: string; size: number }, alreadyAttached: number): string | null {
  if (!isEvtxFileName(file.name)) return EVENT_LOG_WRONG_TYPE_MESSAGE;
  if (file.size > EVTX_MAX_BYTES) return EVENT_LOG_TOO_LARGE_MESSAGE;
  if (file.size === 0) return `Event log '${file.name}' is empty.`;
  if (alreadyAttached >= MAX_EVENT_LOGS_PER_SESSION) return EVENT_LOG_LIMIT_MESSAGE;
  return null;
}

interface UploadedEventLog {
  /** Position in the server session; addresses the log for a later summary. */
  index: number;
  filename: string;
  bytes: number;
  summaryMarkdown: string | null;
  aiGenerated: boolean | null;
}

interface EventLogUploadProps {
  sessionId: string;
}

/** Human-readable size: bytes below 1 KB, KB below 1 MB, otherwise MB. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * Attach Windows Event Logs (.evtx) to an interview session (PI-SPEC-SW-1229).
 *
 * Each upload is validated client-side first (extension, EVTX_MAX_BYTES, and
 * the MAX_EVENT_LOGS_PER_SESSION cap) so an obviously bad file never leaves the
 * browser; the server repeats every check and adds the ElfFile magic test. The
 * summary is opt-in, and the checkbox covers every log in the report: ticking it
 * also summarizes logs added before it was ticked, from the bytes the server
 * already holds. Unticking stops later uploads from being summarized; summaries
 * already made stay. Summaries are shown here only and are deliberately not sent
 * into the interview chat.
 *
 * The uploaded list is local state: the server session is the source of truth
 * for what is filed, and there is no endpoint to remove a staged log.
 *
 * No document paste listener is registered: ScreenshotPaste owns paste for
 * images, and .evtx files cannot be pasted from the clipboard anyway.
 */
export function EventLogUpload({ sessionId }: EventLogUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [summarize, setSummarize] = useState(false);
  const [logs, setLogs] = useState<UploadedEventLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  // What is in flight, for the status line: the file name and what is happening to it.
  const [pending, setPending] = useState<{ name: string; label: string } | null>(null);
  // Session index of the log whose summary is open in the dialog.
  const [viewingIndex, setViewingIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // A ref, not state, so two files offered in one event see each other's claim
  // on the per-session cap before React re-renders.
  const busyRef = useRef(false);
  // Mirrors of state read inside async loops, which would otherwise see stale closures.
  const logsRef = useRef<UploadedEventLog[]>([]);
  const summarizeRef = useRef(false);
  const uploadMutation = useUploadEventLog();
  const summarizeMutation = useSummarizeInterviewEventLog();

  const storeLogs = (next: UploadedEventLog[]) => {
    logsRef.current = next;
    setLogs(next);
  };

  const validationError = eventLogValidationError;

  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;
    if (busyRef.current) {
      setError(EVENT_LOG_BUSY_MESSAGE);
      return;
    }

    busyRef.current = true;
    setError(null);
    // Snapshot the checkbox so a mid-upload toggle cannot change what this
    // batch asked for.
    const summarizeThisBatch = summarize;
    let attached = logs.length;

    try {
      for (const file of files) {
        const problem = validationError(file, attached);
        if (problem) {
          setError(problem);
          // The cap is a hard stop; other problems are per-file.
          if (problem === EVENT_LOG_LIMIT_MESSAGE) break;
          continue;
        }

        setPending({
          name: file.name,
          label: summarizeThisBatch ? "Uploading and summarizing..." : "Uploading...",
        });
        try {
          const response = await uploadMutation.mutateAsync({
            sessionId,
            file,
            summarize: summarizeThisBatch,
          });
          attached += 1;
          storeLogs([...logsRef.current, response.data]);
        } catch (uploadError) {
          setError(uploadError instanceof Error ? uploadError.message : "Event log upload failed");
        }
      }
    } finally {
      busyRef.current = false;
      setPending(null);
    }
  };

  /** Summarize every attached log that has no summary yet, one at a time. */
  const summarizeAttachedLogs = async () => {
    // The checkbox is disabled while anything is in flight, so nothing is skipped here.
    if (busyRef.current) return;
    const missing = logsRef.current.filter((log) => log.summaryMarkdown === null);
    if (missing.length === 0) return;

    busyRef.current = true;
    setError(null);
    try {
      for (const log of missing) {
        setPending({ name: log.filename, label: "Summarizing..." });
        try {
          const response = await summarizeMutation.mutateAsync({ sessionId, index: log.index });
          storeLogs(
            logsRef.current.map((item) => (item.index === response.data.index ? response.data : item))
          );
        } catch (summaryError) {
          setError(
            summaryError instanceof Error ? summaryError.message : "Event log summary failed"
          );
        }
      }
    } finally {
      busyRef.current = false;
      setPending(null);
    }
  };

  const handleSummarizeChange = (checked: boolean) => {
    summarizeRef.current = checked;
    setSummarize(checked);
    if (checked) void summarizeAttachedLogs();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    void handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset so choosing the same file again still fires onChange.
    e.target.value = "";
    void handleFiles(files);
  };

  const isPending = pending !== null;
  const viewingLog = logs.find((log) => log.index === viewingIndex);
  const viewingSummary: EvtxSummaryView | null =
    viewingLog?.summaryMarkdown != null
      ? {
          fileName: viewingLog.filename,
          summaryMarkdown: viewingLog.summaryMarkdown,
          aiGenerated: viewingLog.aiGenerated === true,
          description: "Attached to the case with the log when you submit the report.",
        }
      : null;
  const atLimit = logs.length >= MAX_EVENT_LOGS_PER_SESSION;

  return (
    <div className="space-y-2">
      {/* Drop zone — one line, so the interview's message list keeps its height */}
      <div
        data-testid="event-log-drop-zone"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-lg px-4 py-3 text-center transition-colors",
          isDragging ? "border-primary bg-primary/5" : "border-white/[0.08] hover:border-white/[0.2]"
        )}
      >
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <Upload className="h-4 w-4 flex-shrink-0" />
          <span>
            <strong className="text-foreground">Drop an .evtx event log</strong> or{" "}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isPending || atLimit}
              className="text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
            >
              browse
            </button>
          </span>
          <span className="text-xs">Up to {MAX_EVENT_LOGS_PER_SESSION} logs, 10 MB each</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".evtx"
          multiple
          className="hidden"
          data-testid="event-log-file-input"
          onChange={handleFileInput}
        />
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          id={SUMMARIZE_CHECKBOX_ID}
          checked={summarize}
          onCheckedChange={handleSummarizeChange}
          disabled={isPending}
          aria-describedby={`${SUMMARIZE_CHECKBOX_ID}-hint`}
          className="mt-0.5"
        />
        <div className="text-sm leading-snug">
          <label
            htmlFor={SUMMARIZE_CHECKBOX_ID}
            className="text-muted-foreground cursor-pointer select-none"
          >
            <strong className="text-foreground">Summarize event logs</strong> (AI, optional)
          </label>
          <p id={`${SUMMARIZE_CHECKBOX_ID}-hint`} className="text-xs text-muted-foreground italic">
            Covers every log in this report, including ones already added. Attaches a readable
            summary to the case.
          </p>
        </div>
      </div>

      {pending && (
        <div className="text-xs text-primary" role="status">
          {pending.label} <span className="text-muted-foreground">{pending.name}</span>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {logs.length > 0 && (
        <ul className="space-y-2" aria-label="Uploaded event logs">
          {logs.map((log, index) => (
            <li
              key={`${log.filename}-${String(index)}`}
              className="border border-white/[0.08] rounded-lg p-3"
            >
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="font-medium truncate">{log.filename}</span>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {formatBytes(log.bytes)}
                </span>
                <span className="text-xs text-green-500 flex-shrink-0">Uploaded</span>
              </div>
              {log.summaryMarkdown && (
                <div className="mt-2 flex items-center gap-2">
                  {/* Opens a full-size dialog: the summary's tables are too wide to read in this column. */}
                  <button
                    type="button"
                    onClick={() => {
                      setViewingIndex(log.index);
                    }}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <Maximize2 className="h-3 w-3" />
                    View summary
                  </button>
                  <Badge variant="outline" className="text-[10px]">
                    {log.aiGenerated ? "AI-generated" : "Digest only"}
                  </Badge>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <EvtxSummaryViewDialog
        open={viewingSummary !== null}
        onOpenChange={(open) => {
          if (!open) setViewingIndex(null);
        }}
        summary={viewingSummary}
      />
    </div>
  );
}
